import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useIsAuthenticated } from "@azure/msal-react";
import { HiBeaker } from "react-icons/hi";
import { FaHome, FaClipboardCheck, FaTachometerAlt, FaUserShield, FaRobot } from "react-icons/fa";
import { FaMagnifyingGlass, FaPeopleGroup } from "react-icons/fa6";
import LoginButton from '../auth/LoginButton';
import UserProfile from '../auth/UserProfile';
import ThemeToggle from '../ui/ThemeToggle';
import VersionInfo from './VersionInfo';
import styles from './Header.module.css';
import NotificationsAlert from '../ui/NotificationsAlert';

// Icon mapping for navigation links
const NAV_ICONS = {
    'Home': FaHome,
    'My Dashboard': FaTachometerAlt,
    'Sample Control': FaClipboardCheck,
    'Analysis': HiBeaker,
    'Quality Assurance': FaMagnifyingGlass,
    'Client Management': FaPeopleGroup,
    'Database Assistant': FaRobot,
    'Configurations': FaUserShield
};

// Navigation link configuration
const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'My Dashboard', href: '/dashboard' },
    { name: 'Sample Control', href: '/samples' },
    { name: 'Analysis', href: '/lab' },
    { name: 'Quality Assurance', href: '/quality-assurance' },
    { name: 'Client Management', href: '/clients' },
    { name: 'Database Assistant', href: '/database-assistant', authRequired: true },
    { name: 'Configurations', href: '/configurations', adminOnly: true },
];

// Reusable NavLink component
const NavLink = ({ link, onClick }) => {
    const IconComponent = NAV_ICONS[link.name];
    return (
        <li key={link.name}>
            <Link to={link.href} onClick={onClick}>
                {IconComponent && (
                    <IconComponent 
                        style={{ verticalAlign: 'middle', marginRight: '6px' }} 
                    />
                )}
                {link.name}
            </Link>
        </li>
    );
};

function Header() {
    const [open, setOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const isAuthenticated = useIsAuthenticated();

    // Lock/unlock body scroll when menu opens/closes
    useEffect(() => {
        if (open) {
            // Store current scroll position
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
        } else {
            // Restore scroll position
            const scrollY = document.body.style.top;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
            window.scrollTo(0, parseInt(scrollY || '0') * -1);
        }
        
        // Cleanup on unmount
        return () => {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
        };
    }, [open]);

    // Check if modal is open by checking body attribute
    const isModalOpen = () => {
        return document.body.getAttribute('data-modal-open') === 'true';
    };

    // Handle hamburger click with modal check
    const handleHamburgerClick = () => {
        if (isModalOpen()) {
            // Trigger warning animation is already handled by CSS hover
            return;
        }
        setOpen(!open);
    };

    // Handle collapse toggle with modal check
    const handleCollapseToggle = () => {
        if (isModalOpen()) {
            // Trigger warning animation is already handled by CSS hover
            return;
        }
        setIsCollapsed(!isCollapsed);
    };

    // Update body class when header collapses/expands
    useEffect(() => {
        if (isCollapsed) {
            document.body.classList.add('header-collapsed');
        } else {
            document.body.classList.remove('header-collapsed');
        }
        
        // Cleanup on unmount
        return () => {
            document.body.classList.remove('header-collapsed');
        };
    }, [isCollapsed]);

    return (
        <>
            <div className={styles['header-wrapper']}>
                <div className={`${styles['header-container']} ${isCollapsed ? styles['collapsed'] : ''}`}>
                    <div className={styles['auth-section']}>
                        <img 
                            src="/PrismVial_Small.jpg.png" 
                            alt="Prism Vial" 
                            className={styles['prism-logo']}
                        />
                        <UserProfile />
                        <LoginButton />
                    </div>
                    <div className={styles['right-section']}>
                        <ThemeToggle />
                        {isAuthenticated && <NotificationsAlert />}
                        <div className={styles['hamburger-menu']}>
                            <button
                                className={`${styles['hamburger-icon']}${open ? ` ${styles['open']}` : ''}`}
                                onClick={handleHamburgerClick}
                                aria-label="Open navigation menu"
                            >
                                <span />
                                <span />
                                <span />
                            </button>
                        </div>
                    </div>
                </div>
                <button 
                    className={`${styles['collapse-toggle']} ${isCollapsed ? styles['collapsed'] : ''}`}
                    onClick={handleCollapseToggle}
                    aria-label={isCollapsed ? "Expand header" : "Collapse header"}
                >
                    <span className={styles['arrow']} />
                </button>
            </div>
            
            <div className={`${styles['menu-overlay']}${open ? ` ${styles['show']}` : ''}`}>
                <button
                    className={styles['close-menu-button']}
                    onClick={() => setOpen(false)}
                    aria-label="Close navigation menu"
                >
                    <span />
                    <span />
                </button>
                <nav>
                    <ul>
                        {navLinks
                            .filter(link => !link.authRequired || isAuthenticated)
                            .map(link => (
                                <NavLink 
                                    key={link.name} 
                                    link={link} 
                                    onClick={() => setOpen(false)} 
                                />
                            ))
                        }
                    </ul>
                </nav>
                <div className={styles['menu-version']}>
                    <VersionInfo />
                </div>
            </div>
        </>
    );
}

export default Header;
